import { captureException } from "@sentry/nextjs";
import NextImage from "next/image";
import Script from "next/script";
import { useCallback } from "react";
import { Box } from "~/components-styled/box";
import { Button } from "~/components-styled/button";
import { Paragraph, Text } from "~/components-styled/typography";
import { useSendCategorizedEvent } from "~/lib/analytics";
import { styled } from "~/stitches.config";
import { formatNumber } from "~/utils/format";

type AuditId = keyof typeof auditTypes;

const auditTypes = {
  audit: {
    price: 9700,
    label: "Price From",
    calendlyUrl: "https://calendly.com/baymard/ux-audit",
    email: "audit@baymard.com",
  },
  apparel: {
    price: 12700,
    label: "Apparel Audit Price",
    calendlyUrl: "https://calendly.com/baymard-ins/ux-audit-apparel-intro-call",
    email: "audit@baymard.com?subject=ApparelAudit",
  },
  dtc: {
    price: 12700,
    label: "DTC Audit Price",
    calendlyUrl:
      "https://calendly.com/baymard-ins/ux-audit-online-direct-to-consumer",
    email: "audit@baymard.com?subject=DTCAudit",
  },
  "electronics-and-office": {
    price: 12700,
    label: "Electronics & Office Audit Price",
    calendlyUrl:
      "https://calendly.com/baymard-ins/ux-audit-electronics-office-intro-call",
    email: "audit@baymard.com?subject=ElectronicsOfficeAudit",
  },
  "furniture-and-home-decor": {
    price: 12700,
    label: "Furniture & Home Decor Audit Price",
    calendlyUrl:
      "https://calendly.com/baymard-ins/ux-audit-furniture-home-decor",
    email: "audit@baymard.com?subject=FurnitureHomeDecorAudit",
  },
  "luxury-goods": {
    price: 12700,
    label: "Luxury Goods Audit Price",
    calendlyUrl: "https://calendly.com/baymard-ins/ux-audit-luxury-intro-call",
    email: "audit@baymard.com?subject=LuxuryAudit",
  },
  "mass-merchant-and-department-store": {
    price: 12700,
    label: "Mass Merchant Audit Price",
    calendlyUrl:
      "https://calendly.com/baymard-ins/ux-audit-mass-merchant-intro-call",
    email: "audit@baymard.com?subject=MassMerchantAudit",
  },
  "online-grocery": {
    price: 12700,
    label: "Online Grocery Audit Price",
    calendlyUrl:
      "https://calendly.com/baymard-ins/ux-audit-online-grocery-intro-call",
    email: "audit@baymard.com?subject=GroceryAudit",
  },
  "health-and-beauty": {
    price: 12700,
    label: "Health & Beauty Audit Price",
    calendlyUrl:
      "https://calendly.com/baymard-ins/ux-audit-health-beauty-intro-call",
    email: "audit@baymard.com?subject=HealthBeautyAudit",
  },
  "home-and-hardware": {
    price: 12700,
    label: "Home & Hardware Audit Price",
    calendlyUrl:
      "https://calendly.com/baymard-ins/ux-audit-home-hardware-intro-call",
    email: "audit@baymard.com?subject=HomeHardwareAudit",
  },
  telco: {
    price: 13100,
    label: "Telco Audit Price",
    calendlyUrl: "https://calendly.com/baymard-ins/ux-audit-telco-intro-call",
    email: "audit@baymard.com?subject=TelcoAudit",
  },
  "saas-and-digital-subscriptions": {
    price: 12700,
    label: "Subscriptions Audit Price",
    calendlyUrl:
      "https://calendly.com/baymard-ins/ux-audit-saas-digital-subscriptions",
    email: "audit@baymard.com?subject=SaaS-Digital-Audit",
  },
  "sports-toys-and-hobbies": {
    price: 12700,
    label: "UX Audit Price",
    calendlyUrl:
      "https://calendly.com/baymard-ins/ux-audit-sports-toys-hobbies-intro-call",
    email: "audit@baymard.com?subject=SportsToysHobbiesAudit",
  },
} as const;

export function BookSession({ auditId }: { auditId: AuditId }) {
  const { label, price, email } = auditTypes[auditId];
  const bookSession = useBookSession(auditId);

  return (
    <>
      <Script src="https://assets.calendly.com/assets/external/widget.js" />
      <link
        href="https://assets.calendly.com/assets/external/widget.css"
        rel="stylesheet"
      />

      <BookSessionContainer>
        <Avatar
          src={
            "https://cdn.baymard.com/research/media_files/attachments/42950/original/research-media-file-02e9d58c63aee57695c39795c2bda295.jpg"
          }
        />

        <Box spacing="inherit">
          <Box spacing="y3">
            <Paragraph>
              Talk to UX auditor Laura about your goals and discuss Baymard’s UX
              research methodology.
            </Paragraph>
            <Box>
              <Button size="lg" onClick={bookSession}>
                Schedule a Call
              </Button>{" "}
              <Box
                css={{ display: "block", "@xs": { display: "inline-block" } }}
              >
                or email{" "}
                <EmailLink href={`mailto:${email}`}>
                  {email.split("?")[0]}
                </EmailLink>
              </Box>
            </Box>
          </Box>

          <Box css={{ width: 1, bg: "$slateGray" }} />

          <Box>
            <Paragraph variant="overline1">{label}</Paragraph>
            <Paragraph variant="h1">${formatNumber(price)}</Paragraph>
            <Paragraph variant="body2">17-25 workdays</Paragraph>
          </Box>
        </Box>
      </BookSessionContainer>
    </>
  );
}

export function useBookSession(auditId: AuditId) {
  const sendEvent = useSendCategorizedEvent();
  return useCallback(() => {
    try {
      sendEvent({ action: "click", label: "Schedule a call" });
      // @ts-expect-error
      window.Calendly.initPopupWidget({
        url: auditTypes[auditId].calendlyUrl,
      });
    } catch (err) {
      captureException(err);
    }
  }, [auditId, sendEvent]);
}

const BookSessionContainer = styled("div", {
  textAlign: "center",
  alignItems: "center",
  text: "body1",
  fontSize: "$6r",
  bg: "$blueZodiac",
  color: "white",
  p: "$4",

  borderRadius: "$lg",
  display: "flex",
  spacing: "y2",
  "@xs": {
    spacing: "x3_5",
    textAlign: "unset",
    alignItems: "unset",
  },
});

function Avatar({ src }: { src: string }) {
  return (
    <Box
      css={{
        size: "60px",
        overflow: "hidden",
        position: "relative",
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
        "@xs": {
          minWidth: "60px",
          size: "unset",
          flexBasis: "15%",
        },
      }}
    >
      <Box
        css={{
          position: "relative",
          width: "100%",
          pt: "100%",
          overflow: "hidden",
          borderRadius: "50%",
        }}
      >
        <NextImage alt="" src={src} layout="fill" objectFit="contain" />
      </Box>
    </Box>
  );
}

const EmailLink = styled("a", Text, {
  color: "white",
  fontWeight: "$bold",
  "&:hover": { color: "white", textDecoration: "none" },
  "&:visited": { color: "white" },
});
